import { faLocationDot } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core"
import CloseIcon from "@material-ui/icons/Close"
import { useEffect, useState } from "react"

import {
  buildCategoriesArray,
  mergeCustomEntry,
  updateSelectedItems,
  Custom,
  Section,
  Selected,
} from "../../../utils/safetyPlanUtils"
import { colors } from "../../../utils/theme"

import useSafetyPlanOptionStyles from "./options/useSafetyPlanOptionStyles"
import AddPlaceDialog from "./AddPlaceDialog"
import Categories from "./Categories"

interface EditSafetyPlanProps {
  open: boolean
  onClose: () => void
  data: Map<string, Section>
  selected: string
  onSave: (data: Selected[]) => void
  optionIds: Selected[]
}

/**
 * Modal dialog for editing a single safety plan section.
 *
 * Displays the section's categories and their response options, allowing the
 * user to toggle selections, enter custom text, add contacts, and add places.
 * Custom text entries are tracked separately via `customHandler` and merged
 * into the final selection on save. Place entries are handled through a nested
 * `AddPlaceDialog`.
 *
 * @param open - Whether the dialog is visible.
 * @param onClose - Callback invoked when the dialog is dismissed.
 * @param data - Map of all available safety plan sections keyed by section id.
 * @param selected - The id of the section currently being edited.
 * @param onSave - Callback invoked with the final flat list of selected options on save.
 * @param optionIds - The pre-existing selections to initialise the modal with.
 */
export default function EditSafetyPlan({
  onClose,
  open,
  data,
  selected,
  onSave,
  optionIds,
}: EditSafetyPlanProps) {
  const sharedStyles = useSafetyPlanOptionStyles()
  const localStyles = useStyles()
  const classes = { ...sharedStyles, ...localStyles }
  const [selectedItems, setSelectedItems] = useState<Selected[]>([])
  const [custom, setCustom] = useState<Custom>()
  const [openPlace, setOpenPlace] = useState(false)

  useEffect(() => {
    setSelectedItems(optionIds)
  }, [optionIds])

  useEffect(() => {
    if (open) {
      setCustom(undefined)
    }
  }, [open])

  const clickHandler = (id: string, value: Selected["value"], type: string) => {
    setSelectedItems((prev) => updateSelectedItems(prev, id, value, type))
  }

  const customHandler = (id: string, value: string[], type: string) => {
    setCustom({ id: id, value: value, category_type: type })
  }

  const submitHandler = () => {
    const finalItems =
      custom != null
        ? mergeCustomEntry(selectedItems, custom)
        : [...selectedItems]
    setSelectedItems(finalItems)
    setCustom(undefined)
    onSave(finalItems)
    onClose()
  }

  const category = data.get(selected)
  const { categoriesArr, placeCategory, foundContact, foundPlace } =
    buildCategoriesArray(category)

  const handleSavePlace = (place: string) => {
    if (!placeCategory) return
    const id = placeCategory.response_options[0].id
    clickHandler(id, [{ place }], "place")
    setOpenPlace(false)
  }

  const handleAddPlace = () => {
    setOpenPlace(true)
  }

  const handleClosePlace = () => {
    setOpenPlace(false)
  }

  const categories = categoriesArr.map((d) => (
    <Categories
      category={d}
      onClick={clickHandler}
      selection={selectedItems}
      onChange={customHandler}
      skipIfPlace
      key={d.category_id}
    />
  ))

  let descriptionText = category?.description
  if (category?.header.includes("Myself Safe") && descriptionText) {
    descriptionText = descriptionText.replace(/<br\s*\/?>/gi, "")
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        disableScrollLock
        classes={{
          paper: classes.editContainer,
        }}
      >
        <DialogContent>
          <div className={classes.editModal}>
            <IconButton
              id="closeEditModal"
              className={classes.closeEditModalContainer}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
            <div className={classes.headerContainer}>
              <Typography className={classes.editingText}>EDITING</Typography>
              <Typography
                className={classes.headerText}
                component="div"
                dangerouslySetInnerHTML={{ __html: category?.header || "" }}
              />
              <Typography
                className={classes.descriptionText}
                component="div"
                dangerouslySetInnerHTML={{
                  __html: descriptionText ?? "",
                }}
              />

              <div className={classes.divider}></div>
            </div>
            {!foundContact && (
              <Typography className={classes.selectOptionsContainerText}>
                Select any options that apply
              </Typography>
            )}
            {categories}
          </div>
        </DialogContent>
        <DialogActions
          style={{ alignSelf: "stretch", flexDirection: "column", gap: "8px" }}
        >
          {foundPlace && (
            <Button
              onClick={handleAddPlace}
              className={classes.addPlaceModalBtn}
            >
              <FontAwesomeIcon
                icon={faLocationDot}
                className={classes.plusColor}
              />
              <Typography className={classes.addPlaceDialogActionTxt}>
                Add a Place
              </Typography>
            </Button>
          )}
          <Button onClick={submitHandler} className={classes.saveModalBtn}>
            <Typography
              className={classes.btnText}
              style={{ color: colors.neutral.white }}
            >
              Save
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>
      <AddPlaceDialog
        open={openPlace}
        onClose={handleClosePlace}
        onSave={handleSavePlace}
      />
    </>
  )
}

const useStyles = makeStyles(() => ({
  editContainer: {
    display: "flex",
    padding: "36px 18px 10px 18px",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    alignSelf: "stretch",
  },
  divider: {
    height: "2px",
    alignSelf: "stretch",
    background: colors.safetyPlan.grayscale.black10,
  },
  categoryName: {
    flex: "1 0 0",
    color: colors.neutral.asphalt,
    textAlign: "center",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: "24px",
  },
  distractionSupportContactTxt: {
    color: colors.neutral.asphalt,
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "20px",
    alignSelf: "stretch",
  },
  selectOptionsContainerText: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    alignSelf: "stretch",
    marginTop: "16px",
    flex: "1 0 0",
    color: colors.neutral.asphalt,
    textAlign: "center",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: "24px",
  },
  descriptionText: {
    color: colors.neutral.asphalt,
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "20px",
  },
  headerText: {
    color: colors.safetyPlan.ink,
    fontSize: "30px",
    fontWeight: 700,
    lineHeight: "40px",
  },
  editingText: {
    alignSelf: "stretch",
    color: colors.neutral.cement,
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: "16px",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "5px",
  },
  closeEditModalContainer: {
    display: "flex",
    width: "36px",
    padding: "10px",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    position: "absolute",
    right: "16px",
    top: "16px",
    borderRadius: "360px",
    background: colors.safetyPlan.grayscale.white75,
  },
  editModal: {
    display: "flex",
    padding: "0px 16px",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
  },
  categoriesContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    alignSelf: "stretch",
    paddingTop: "10px",
  },
}))
